import WebMap from '@arcgis/core/WebMap';
import type { PropsWithChildren } from 'react';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { useAppState } from '@/overmind';

const EsriMapContext = createContext(null as unknown as WebMap | undefined);

const useArcGisWebMapConfiguration = () => {
  return useAppState().arcgis.config.value?.map.webMap;
};

const usePreventStrictModeInitialCall = (map: WebMap | undefined) => {
  useEffect(() => {
    return () => {
      // The condition prevents React.StrictMode's initial call of the effect's return cb.
      if (map?.loaded) map.destroy();
    };
  }, [map]);
};

export const useMap = () => useContext(EsriMapContext);

export const MapProvider = ({ children }: PropsWithChildren) => {
  const webMapConfig = useArcGisWebMapConfiguration();
  const appConfig = useAppState().config;
  const auth = useAppState().auth;

  const [map, setMap] = useState<WebMap | undefined>(undefined);

  useEffect(() => {
    if (auth.current !== 'AUTHENTICATED') return;
    if (!webMapConfig) return;
    if (!map) {
      const webmap = new WebMap({
        portalItem: {
          id: webMapConfig.id,
          portal: {
            url: webMapConfig.url
          }
        }
      });
      webmap.loadAll().then(() => {
        setMap(webmap);
      }).catch((error) => {
        console.warn('WebMap loadAll error (some layers may not load):', error);
        setMap(webmap);
      });
    }
  }, [webMapConfig, auth.current]);

  usePreventStrictModeInitialCall(map);

  if (appConfig.type === 'NOT_AVAILABLE' || auth.current !== 'AUTHENTICATED')
    return null;

  return (
    <EsriMapContext.Provider value={map}>{children}</EsriMapContext.Provider>
  );
};
